// Retail Trust embed auth — browser-side OAuth "b2b" code exchange with S256 PKCE:
// the code_challenge is BASE64URL(SHA-256(code_verifier)). Must match the partner's
// authorize call, which sends code_challenge_method=S256.
//
// Flow (per Retail Trust's b2b/token spec + the agreed postMessage contract):
//   1. up-spa generates a random string, keeps it as the code_verifier.
//   2. up-spa → RT:  postMessage({ type: "sl:ready", code_challenge }) — code_challenge
//      == base64url(sha256(verifier)); targeted at the Retail Trust origin (never "*").
//   3. RT backend mints a single-use code (~60s TTL, via /api/v2/oauth/b2b/authorize)
//      binding it to this code_challenge with code_challenge_method=S256.
//   4. RT → up-spa:  postMessage({ type: "sl:code", code }) — validated against the
//      Retail Trust origin + window.parent on receipt.
//   5. up-spa → AuthServer2: POST /api/v2/oauth/b2b/token (JSON) with
//      { grant_type, code, redirect_uri, code_verifier, client_id? } → { access_token }.
//      redirect_uri must exactly match what the partner registered at /b2b/authorize —
//      it is partner-specific (see PARTNER_CONFIGS below), not a single global value.
//      client_id is optional: the backend does not require it at this stage, so it is
//      only sent when a partner config actually sets one.
//   6. The access_token is an OAuth token, NOT a CORE-API session on its own (posting
//      it directly as user_id fails "Error 3994: Not a valid user"). It is handed to
//      onToken, which resolves it into a session via POST
//      /application/api/u/user/resolve-token { token, source } → the full AuthSuccess
//      consumed by setUser (see the retailTrustSignIn store action).

import { isZurichEmbed } from "./embed";
import { postToHost } from "./zurichEmbed";

const AUTH_SERVER = process.env.VUE_APP_AUTHSERVER_PATH || "https://sso.surge-qa.com";
const TOKEN_URL = process.env.VUE_APP_RT_TOKEN_URL || `${AUTH_SERVER}/api/v2/oauth/b2b/token`;

// Per-partner exchange config, keyed by the partner's embedding origin(s). redirect_uri
// is partner-specific — it must be byte-identical to what that partner registered at
// /b2b/authorize — so it cannot be a single constant once more than one partner embeds
// us. client_id is optional; omit it (leave undefined) unless a partner's backend
// requires one. Add an entry here per new embed partner; the exchange picks the right
// one from the embedding origin, with the first entry as the single-partner fallback.
type PartnerConfig = {
  origins: string[];
  redirectUri: string;
  clientId?: string;
};

const PARTNER_CONFIGS: PartnerConfig[] = [
  {
    origins: (
      process.env.VUE_APP_RT_PARTNER_ORIGINS ||
      "https://retailtrust.org.uk,https://dev.retailtrust.org.uk,https://dp.retailtrust.org.uk,https://localhost:3000"
    )
      .split(",")
      .map((origin) => origin.trim())
      .filter(Boolean),
    // Code default matches the QA registration (sso.surge-qa.com) that dev/staging
    // test against. Production overrides via VUE_APP_RT_REDIRECT_URI in
    // .env.production — must stay in sync with whatever RT registers as their prod
    // redirect_uri (currently https://retailtrust.spectrum.life/callback).
    redirectUri: process.env.VUE_APP_RT_REDIRECT_URI || "https://retailtrust.surge-qa.com/callback",
    clientId: process.env.VUE_APP_RT_CLIENT_ID || undefined,
  },
];

// Every allowed embedding origin, across all partner configs — used to validate the
// inbound sl:code message's event.origin.
const ALLOWED_PARTNER_ORIGINS = PARTNER_CONFIGS.flatMap((partnerConfig) => partnerConfig.origins);

const partnerConfigForOrigin = (origin: string): PartnerConfig =>
  PARTNER_CONFIGS.find((partnerConfig) => partnerConfig.origins.includes(origin)) ?? PARTNER_CONFIGS[0];

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// The PKCE code_verifier: a high-entropy random string.
const generateCodeVerifier = (): string => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
};

// S256 PKCE: code_challenge = BASE64URL(SHA-256(ASCII(code_verifier))).
const deriveCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return toBase64Url(new Uint8Array(digest));
};

// Best-effort detection of the embedding partner's origin, used both to target
// sl:ready (rather than "*") and to pick that partner's redirect_uri/client_id for
// the token exchange. Falls back to the allow-list / first config (posting to a
// non-matching origin is a browser no-op, so no broadcast; and with a single
// partner configured, guessing wrong at the exchange step isn't possible anyway).
const resolvePartnerOrigin = (): string | undefined => {
  const ancestors = window.location.ancestorOrigins;
  const first = ancestors && ancestors.length ? ancestors[0] : "";
  if (ALLOWED_PARTNER_ORIGINS.includes(first)) return first;
  try {
    const referrerOrigin = document.referrer ? new URL(document.referrer).origin : "";
    if (ALLOWED_PARTNER_ORIGINS.includes(referrerOrigin)) return referrerOrigin;
  } catch (e) {
    // malformed referrer — fall through
  }
  return undefined;
};

const partnerTargets = (resolvedOrigin: string | undefined): string[] =>
  resolvedOrigin ? [resolvedOrigin] : ALLOWED_PARTNER_ORIGINS;

// Tell the embedding partner that something happened in the app they should react to
// — e.g. a booking completed, so their page can close the iframe or refresh its own
// UI. Targeted at the partner's exact origin (never "*"). No-op when we aren't
// embedded, so callers don't need to guard.
export const notifyPartner = (message: Record<string, unknown>): void => {
  if (window.parent === window.self) return;
  // Zurich's origins come from VUE_APP_EMBED_HOST_ORIGINS, not the Retail Trust list.
  if (isZurichEmbed()) {
    postToHost(message);
    return;
  }
  partnerTargets(resolvePartnerOrigin()).forEach((origin) => {
    window.parent.postMessage(message, origin);
  });
};

type PushRouter = { push: (location: string) => void };

// Send the member "home" from an exit point (end of a flow, an error screen, a
// cancelled booking). Embedded, home is the HOST's page — the host owns navigation,
// so we hand it an event and stop; routing to our own home would strand the member
// inside an iframe the host is about to close. Standalone, it's our own home route.
export const returnHome = (
  router: PushRouter,
  message: Record<string, unknown> = { type: "sl:close", v: 1 }
): void => {
  if (window.parent !== window.self) {
    notifyPartner(message);
    return;
  }
  router.push("/");
};

const exchangeCodeForToken = async (
  code: string,
  codeVerifier: string,
  partnerConfig: PartnerConfig
): Promise<string | null> => {
  try {
    const res = await fetch(TOKEN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "authorization_code",
        code,
        redirect_uri: partnerConfig.redirectUri,
        code_verifier: codeVerifier,
        ...(partnerConfig.clientId ? { client_id: partnerConfig.clientId } : {}),
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.error || !body.access_token) {
      // Surface the auth server's exact reason (e.g. invalid_grant + a description
      // naming PKCE/redirect_uri/code mismatch) so a partner can debug from the
      // iframe console rather than a silent failed login.
      console.warn(
        "[partnerEmbed] token exchange failed",
        { status: res.status, error: body.error, error_description: body.error_description }
      );
      return null;
    }
    return body.access_token as string;
  } catch (e) {
    console.warn("[partnerEmbed] token exchange threw", e);
    return null;
  }
};

export type RetailTrustHandlers = {
  onToken: (accessToken: string) => void;
  onError?: (reason: string) => void;
};

// Announce readiness (with the code_challenge) to the parent, wait for the code,
// then exchange it. Returns an unsubscribe. Call once at startup when embedded.
export const startRetailTrustAuth = (handlers: RetailTrustHandlers): (() => void) => {
  const codeVerifier = generateCodeVerifier();
  // Resolved once per session: which partner is embedding us decides both where
  // sl:ready is targeted and which redirect_uri/client_id the exchange uses.
  const resolvedOrigin = resolvePartnerOrigin();
  const partnerConfig = partnerConfigForOrigin(resolvedOrigin ?? PARTNER_CONFIGS[0].origins[0]);

  const listener = (event: MessageEvent): void => {
    // Security-critical: only accept the code from a known partner origin and
    // from our actual embedder.
    if (!ALLOWED_PARTNER_ORIGINS.includes(event.origin)) return;
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!data || typeof data !== "object" || data.type !== "sl:code") return;
    if (typeof data.code !== "string" || data.code.length === 0) return;
    // The raw verifier is sent at the token step; the server hashes it (S256) and
    // compares to the challenge the partner registered at authorize.
    exchangeCodeForToken(data.code, codeVerifier, partnerConfig).then((accessToken) => {
      if (accessToken) handlers.onToken(accessToken);
      else handlers.onError?.("token exchange failed");
    });
  };
  window.addEventListener("message", listener);

  // sl:ready — carries the S256 challenge, targeted at the resolved partner origin
  // (never "*"). The digest is async, but the listener is already attached, so a fast
  // sl:code reply can't be missed (the parent only replies after receiving sl:ready).
  deriveCodeChallenge(codeVerifier).then((codeChallenge) => {
    partnerTargets(resolvedOrigin).forEach((origin) => {
      window.parent.postMessage({ type: "sl:ready", code_challenge: codeChallenge }, origin);
    });
  });

  return () => window.removeEventListener("message", listener);
};
